// CommissionScreen2.jsx

import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Axios from "axios";

import DataLoader from "../components/DataLoader";
import {
    Container,
    FormControl,
    Grid,
    MenuItem,
    Select,
    Stack,
    Typography,
} from "@mui/material";

import ToggleButtonTitled from "../components/ToggleButtonTitled";

import { setJS } from "../actions/globalReportFilters";
import {
    DATATYPE_CHECKIN,
    DATATYPE_CHECKIN_NAME,
    DATATYPE_CHECKOUT,
    DATATYPE_CHECKOUT_NAME,
    DATATYPE_RESDATE,
    DATATYPE_RESDATE_NAME,
    formatDate,
    GROUP_DATE_MONTH,
    GROUP_DATE_YEAR,
    PRESET_BOOKING_GROUP,
    PRESET_CHOOSE_MONTH,
    PRESET_CUSTOM,
    PRESET_THIS_YEAR,
    PresetDates,
} from "../helpers/filters";

import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import "dayjs/locale/en-ie";
import DateRangePicker from "../components/DateRangePicker";
import FiltersByClient from "../components/FiltersByClient";
import ReportTableMetricsSummary from "../components/shared/ReportBuilder/ReportTableMetricsSummary";
import ReportChartBarsCategory from "../components/shared/ReportBuilder/ReportChartBarsCategory";
import ReportChartStackedMonth from "../components/ReportChartStackedMonth";
import CommissionTable from "../components/CommissionTable";
import ActiveFiltersLine from "../components/shared/ReportBuilder/ActiveFiltersLine";
import { URLAPI } from "../configuration";

dayjs.extend(utc);
dayjs.locale("en-ie");

const ITEM_HEIGHT = "50px";

// Util: centralize access to from/to in the same shape
const getFromTo = (js) => ({
    fromDay: js.fromDay ?? js.FromDay ?? js.from ?? "",
    toDay: js.toDay ?? js.ToDay ?? js.to ?? "",
});

// Build an array of { year, month } between fromDay and toDay inclusive
const buildMonthSpan = (fromStr, toStr) => {
    const start = dayjs(fromStr).startOf("month");
    const end = dayjs(toStr).endOf("month");
    const months = [];
    let cursor = start;
    while (cursor.isBefore(end) || cursor.isSame(end, "month")) {
        months.push({ year: cursor.year(), month: cursor.month() }); // 0-based month
        cursor = cursor.add(1, "month");
    }
    return months;
};

// Precompute index for faster writes
const buildMonthIndex = (monthSpan) => {
    const map = new Map();
    monthSpan.forEach((m, idx) => map.set(`${m.year}-${m.month}`, idx));
    return map;
};

const buildMonthSpanAllYears = (yearsObj) => {
    const months = [];
    Object.keys(yearsObj)
        .filter((y) => !Number.isNaN(Number(y)))
        .forEach((y) => {
            const year = Number(y);
            for (let m = 0; m < 12; m += 1) months.push({ year, month: m });
        });
    return months;
};

const getVal = (arr, idx) =>
    Array.isArray(arr) && arr[idx] != null ? arr[idx] : 0;

const getDisplayName = (obj, id) =>
    obj?.SiteName ?? obj?.HotelName ?? `Site ${id}`;

// Build data for Top Commission Clients chart, honoring hotel/currency filters
const buildTopCommissionClientsData = ({
    dataCommission,
    dateType,
    monthSpan,
    selectedHotels,
    selectedCurrencies, // three-letter codes ("EUR", "GBP"...)
    accountManagersHotels,
    metricKey = "commissionNotCancelled",
}) => {
    const yearsObj = dataCommission.data ?? {};
    const dateBlock = dateType;

    const clientsTotals = new Map(); // SiteID -> { current, lastYear, name, currency }

    monthSpan.forEach(({ year, month }) => {
        const yearData = yearsObj[year]?.data ?? {};
        Object.keys(yearData).forEach((siteKey) => {
            if (Number.isNaN(Number(siteKey))) return; // skip metadata nodes
            const siteID = Number(siteKey);
            const siteObj = yearData[siteKey];
            const hotelID = siteObj.HotelID;

            // Apply filters
            const siteName = getDisplayName(siteObj, siteID);
            const baseCurrency = siteObj?.base_currency ?? "EUR";
            if (selectedHotels.length && !selectedHotels.includes(siteName))
                return;
            if (
                selectedCurrencies.length &&
                !selectedCurrencies.includes(baseCurrency)
            )
                return;
            if (
                accountManagersHotels.length &&
                !accountManagersHotels.includes(hotelID)
            ) {
                console.log("TOPCOMM DAt", { accountManagersHotels });
                return;
            }
            const siteYearSection = siteObj?.[dateBlock] ?? {};
            const totalArr =
                (dateType === DATATYPE_RESDATE
                    ? siteYearSection?.TotalCommission_All
                    : siteYearSection?.TotalCommission_NoCanc) ?? [];
            const totalVal = getVal(totalArr, month);

            const prevSiteObj = yearsObj[year - 1]?.data?.[siteKey];
            const prevSection = prevSiteObj?.[dateBlock] ?? {};
            const prevTotalArr =
                (dateType === DATATYPE_RESDATE
                    ? prevSection?.TotalCommission_All
                    : prevSection?.TotalCommission_NoCanc) ?? [];
            const totalValLY = getVal(prevTotalArr, month);

            if (!clientsTotals.has(siteID)) {
                clientsTotals.set(siteID, {
                    current: 0,
                    lastYear: 0,
                    name: siteName,
                    currency: baseCurrency,
                });
            }
            const acc = clientsTotals.get(siteID);
            acc.current += totalVal;
            acc.lastYear += totalValLY;
        });
    });

    const outData = {};
    for (const [
        siteID,
        { current, lastYear, name },
    ] of clientsTotals.entries()) {
        outData[siteID] = {
            // Keep only the keys used by the chart. Year/Month/Day were constant and not used.
            keys: [["SiteName", name]],
            result: { [metricKey]: current },
            resultOneYearBefore: { [metricKey]: lastYear },
        };
    }
    return outData;
};

const buildMonthlyStackedData = ({
    dataCommission,
    dateType,
    monthSpan,
    selectedHotels,
    selectedCurrencies,
    accountManagersHotels,
    metricKey = "commissionNotCancelled",
}) => {
    const yearsObj = dataCommission.data ?? {};
    const dateBlock = dateType;

    const out = {};
    monthSpan.forEach(({ year, month }) => {
        const yearData = yearsObj[year]?.data ?? {};
        let monthTotal = 0;

        Object.keys(yearData).forEach((siteKey) => {
            if (Number.isNaN(Number(siteKey))) return;
            const siteObj = yearData[siteKey];
            const siteName = getDisplayName(siteObj, "");
            const hotelID = siteObj.HotelID;
            const baseCurrency = siteObj?.base_currency ?? "EUR";

            if (selectedHotels.length && !selectedHotels.includes(siteName))
                return;
            if (
                selectedCurrencies.length &&
                !selectedCurrencies.includes(baseCurrency)
            )
                return;
            if (
                accountManagersHotels.length &&
                !accountManagersHotels.includes(hotelID)
            )
                return;
            const section = siteObj?.[dateBlock] ?? {};
            const totalArr =
                (dateType === DATATYPE_RESDATE
                    ? section?.TotalCommission_All
                    : section?.TotalCommission_NoCanc) ?? [];
            monthTotal += getVal(totalArr, month);
        });

        const key = `${month + 1}/${year}`; // TODO: confirm the consumer expects this shape
        out[key] = { result: { [metricKey]: monthTotal } };
    });

    return out;
};

// Compute current + LY commission totals for a single calendar month,
// applying the same hotel/currency/account-manager filters as the main useEffect.
const computeMonthCommission = (
    year,
    month,
    {
        dataCommission,
        dateType,
        selectedHotels,
        selectedCurrencies,
        accountManagersHotels,
    },
) => {
    const yearsObj = dataCommission.data ?? {};
    const yearData = yearsObj[year]?.data ?? {};
    const dateBlock = dateType;
    let total = 0,
        rooms = 0,
        vouchers = 0;
    let totalLY = 0,
        roomsLY = 0,
        vouchersLY = 0;

    Object.keys(yearData).forEach((siteKey) => {
        if (Number.isNaN(Number(siteKey))) return;
        const siteObj = yearData[siteKey];
        const hotelID = siteObj.HotelID;
        const siteName = getDisplayName(siteObj, Number(siteKey));
        const currency = siteObj?.base_currency ?? "EUR";

        if (selectedHotels.length && !selectedHotels.includes(siteName)) return;
        if (selectedCurrencies.length && !selectedCurrencies.includes(currency))
            return;
        if (
            accountManagersHotels.length &&
            !accountManagersHotels.includes(hotelID)
        )
            return;

        const section = siteObj?.[dateBlock] ?? {};
        const prevSection =
            yearsObj[year - 1]?.data?.[siteKey]?.[dateBlock] ?? {};

        total += getVal(
            (dateType === DATATYPE_RESDATE
                ? section.TotalCommission_All
                : section.TotalCommission_NoCanc) ?? [],
            month,
        );
        rooms += getVal(
            (dateType === DATATYPE_RESDATE
                ? section.RoomsCommission_All
                : section.RoomsCommission_NoCanc) ?? [],
            month,
        );
        vouchers += getVal(
            (dateType === DATATYPE_RESDATE
                ? section.VouchersCommission_All
                : section.VouchersCommission_NoCanc) ?? [],
            month,
        );
        totalLY += getVal(
            (dateType === DATATYPE_RESDATE
                ? prevSection.TotalCommission_All
                : prevSection.TotalCommission_NoCanc) ?? [],
            month,
        );
        roomsLY += getVal(
            (dateType === DATATYPE_RESDATE
                ? prevSection.RoomsCommission_All
                : prevSection.RoomsCommission_NoCanc) ?? [],
            month,
        );
        vouchersLY += getVal(
            (dateType === DATATYPE_RESDATE
                ? prevSection.VouchersCommission_All
                : prevSection.VouchersCommission_NoCanc) ?? [],
            month,
        );
    });

    return { total, rooms, vouchers, totalLY, roomsLY, vouchersLY };
};

const CommissionScreen2 = () => {
    const js = useSelector((state) => state.globalReportFilters);
    const { dataCommission } = useSelector((state) => state);
    const { auth } = useSelector((state) => state);
    const dispatch = useDispatch();

    const [dataAccountManagers, setDataAccountManagers] = useState({
        status: "empty",
    });

    const fetchAccountManagers = () => {
        const headers = { Authorization: `Bearer ${auth.user.jwt}` };

        Axios.get(`${URLAPI}/hotels/managers`, { headers })
            .then((res) => {
                const auxDataAccountManagers = {
                    data: res.data.data,

                    status: "loaded",
                };

                setDataAccountManagers(auxDataAccountManagers);
            })
            .catch((err) => {
                setDataAccountManagers({ status: "Error" });
                console.error(err);
            });
    };

    // Init preset on mount
    useEffect(() => {
        const { fromDay, toDay } = PresetDates(PRESET_THIS_YEAR);
        dispatch(
            setJS({ ...js, fromDay, toDay, presetDate: PRESET_THIS_YEAR }),
        );
        fetchAccountManagers();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const [dateType, setDateType] = useState(DATATYPE_CHECKOUT);
    const [openDatePicker, setOpenDatePicker] = useState(false); // Now really used when selecting custom

    // Store codes only ("EUR", "GBP"...). Display layer will format with symbol.
    const [selectedHotels, setSelectedHotels] = useState([]);
    const [selectedCurrencies, setSelectedCurrencies] = useState([]);
    const [selectedAccountManagers, setSelectedAccountManagers] = useState([]);

    const handleSelectedHotels = (hotels) => setSelectedHotels(hotels);
    const handleSelectedCurrencies = (curr) => setSelectedCurrencies(curr);

    const handleSelectedAccountManagers = (ams) =>
        setSelectedAccountManagers(ams);

    const hotelsList = useMemo(() => {
        if (dataCommission.status !== "loaded") return [];
        const names = new Set();
        Object.values(dataCommission.data ?? {}).forEach((yearObj) => {
            Object.values(yearObj.data ?? {}).forEach((siteObj, siteID) => {
                // --- MOD: use helper ------------------------------------------------
                const label = getDisplayName(siteObj, siteID);
                if (label) names.add(label);
                // --------------------------------------------------------------------
            });
        });
        return Array.from(names).sort();
    }, [dataCommission]);

    // Consistent currencies source; assume extra.currencies is a map { "EUR": "€", ... }
    const currenciesMap = useMemo(() => {
        if (dataCommission?.status !== "loaded") return {};
        return (
            dataCommission.extra?.currencies ??
            dataCommission.data?.currencies ??
            {}
        );
    }, [dataCommission]);

    // Build object for Account Managers { am_id: {name: "", hotels: [] }, .... }
    const accountManagers = useMemo(() => {
        if (dataAccountManagers.status !== "loaded") return {};
        const auxAccountsManagers = {};
        (dataAccountManagers.data ?? []).forEach((am) => {
            const { manager_id, manager_name, hotel_id } = am;

            if (!auxAccountsManagers[manager_id]) {
                auxAccountsManagers[manager_id] = {
                    name: manager_name,
                    hotels: [],
                };
            }

            auxAccountsManagers[manager_id].hotels.push(hotel_id);
        });

        return auxAccountsManagers;
    }, [dataAccountManagers]);

    const accountManagersHotels = useMemo(() => {
        console.log("accountManagersHotels MEMO", {
            selectedAccountManagers,
            accountManagers,
        });
        if (
            !selectedAccountManagers.length ||
            !Object.keys(accountManagers).length
        )
            return [];

        return selectedAccountManagers.flatMap(
            (amID) => accountManagers[amID]?.hotels ?? [],
        );
    }, [selectedAccountManagers, accountManagers]);

    const { minYear, maxYearNext } = useMemo(() => {
        if (dataCommission?.status !== "loaded")
            return { minYear: undefined, maxYearNext: undefined };

        const yl =
            dataCommission.yearsLoaded ??
            dataCommission.data?.yearsLoaded ??
            dataCommission.extra?.yearsLoaded ??
            null;

        if (!yl) return { minYear: undefined, maxYearNext: undefined };

        const years = Object.keys(yl)
            .filter((y) => yl[y])
            .map((y) => Number(y))
            .filter((y) => Number.isFinite(y));

        if (!years.length)
            return { minYear: undefined, maxYearNext: undefined };

        const minY = Math.min(...years);
        const maxY = Math.max(...years);
        return { minYear: minY, maxYearNext: maxY + 1 };
    }, [dataCommission]);

    // Global and per-hotel aggregates
    const [commissionGlobal, setCommissionGlobal] = useState({
        commissionTotal: 0,
        commissionRooms: 0,
        commissionVouchers: 0,
        commissionTotalLastYear: 0,
        commissionRoomsLastYear: 0,
        commissionVouchersLastYear: 0,
    });

    useEffect(() => {
        if (dataCommission.status !== "loaded") return;
        const { fromDay, toDay } = getFromTo(js);
        if (!fromDay || !toDay) return;

        const monthSpan = buildMonthSpan(fromDay, toDay);
        const monthIdxMap = buildMonthIndex(monthSpan); // O(1) lookups

        const dateBlock = dateType;
        const yearsObj = dataCommission.data ?? {};

        const globalTotals = {
            commissionTotal: 0,
            commissionRooms: 0,
            commissionVouchers: 0,
            commissionTotalLastYear: 0,
            commissionRoomsLastYear: 0,
            commissionVouchersLastYear: 0,
        };

        const hotelsResult = {};

        monthSpan.forEach(({ year, month }) => {
            const yearData = yearsObj[year]?.data ?? {};
            Object.keys(yearData).forEach((siteKey) => {
                if (Number.isNaN(Number(siteKey))) return;
                const siteID = Number(siteKey);
                const siteObj = yearData[siteKey];
                const hotelID = siteObj.HotelID;

                const siteName = getDisplayName(siteObj, siteID);
                const currency = siteObj?.base_currency ?? "EUR";
                if (selectedHotels.length && !selectedHotels.includes(siteName))
                    return;
                if (
                    selectedCurrencies.length &&
                    !selectedCurrencies.includes(currency)
                )
                    return;
                if (
                    accountManagersHotels.length &&
                    !accountManagersHotels.includes(hotelID)
                )
                    return;

                const section = siteObj?.[dateBlock] ?? {};
                const roomsVal = getVal(
                    (dateType === DATATYPE_RESDATE
                        ? section?.RoomsCommission_All
                        : section?.RoomsCommission_NoCanc) ?? [],
                    month,
                );
                const totalVal = getVal(
                    (dateType === DATATYPE_RESDATE
                        ? section?.TotalCommission_All
                        : section?.TotalCommission_NoCanc) ?? [],
                    month,
                );
                const vouchersVal = getVal(
                    (dateType === DATATYPE_RESDATE
                        ? section?.VouchersCommission_All
                        : section?.VouchersCommission_NoCanc) ?? [],
                    month,
                );

                const prevSection =
                    yearsObj[year - 1]?.data?.[siteKey]?.[dateBlock] ?? {};
                const roomsValLY = getVal(
                    (dateType === DATATYPE_RESDATE
                        ? prevSection?.RoomsCommission_All
                        : prevSection?.RoomsCommission_NoCanc) ?? [],
                    month,
                );
                const totalValLY = getVal(
                    (dateType === DATATYPE_RESDATE
                        ? prevSection?.TotalCommission_All
                        : prevSection?.TotalCommission_NoCanc) ?? [],
                    month,
                );
                const vouchersValLY = getVal(
                    (dateType === DATATYPE_RESDATE
                        ? prevSection?.VouchersCommission_All
                        : prevSection?.VouchersCommission_NoCanc) ?? [],
                    month,
                );

                if (!hotelsResult[siteID]) {
                    const length = monthSpan.length;
                    hotelsResult[siteID] = {
                        commissionTotal: new Array(length).fill(0),
                        commissionRooms: new Array(length).fill(0),
                        commissionVouchers: new Array(length).fill(0),
                        commissionTotalLastYear: new Array(length).fill(0),
                        commissionRoomsLastYear: new Array(length).fill(0),
                        commissionVouchersLastYear: new Array(length).fill(0),
                    };
                }

                const idx = monthIdxMap.get(`${year}-${month}`);
                if (idx != null) {
                    hotelsResult[siteID].commissionTotal[idx] += totalVal;
                    hotelsResult[siteID].commissionRooms[idx] += roomsVal;
                    hotelsResult[siteID].commissionVouchers[idx] += vouchersVal;

                    hotelsResult[siteID].commissionTotalLastYear[idx] +=
                        totalValLY;
                    hotelsResult[siteID].commissionRoomsLastYear[idx] +=
                        roomsValLY;
                    hotelsResult[siteID].commissionVouchersLastYear[idx] +=
                        vouchersValLY;
                }

                globalTotals.commissionTotal += totalVal;
                globalTotals.commissionRooms += roomsVal;
                globalTotals.commissionVouchers += vouchersVal;
                globalTotals.commissionTotalLastYear += totalValLY;
                globalTotals.commissionRoomsLastYear += roomsValLY;
                globalTotals.commissionVouchersLastYear += vouchersValLY;
            });
        });

        console.log("MonthSpan", { monthSpan });

        // Optional: keep an aggregate siteID 0 representing the global sum per month
        if (!hotelsResult[0]) {
            const length = monthSpan.length;
            hotelsResult[0] = {
                commissionTotal: new Array(length).fill(0),
                commissionRooms: new Array(length).fill(0),
                commissionVouchers: new Array(length).fill(0),
                commissionTotalLastYear: new Array(length).fill(0),
                commissionRoomsLastYear: new Array(length).fill(0),
                commissionVouchersLastYear: new Array(length).fill(0),
            };
            // Populate with global totals distributed per month if required.
            // Currently left as zeros to avoid duplicating totals; adjust if the consumer expects filled arrays.
        }

        setCommissionGlobal(globalTotals);
    }, [
        dataCommission,
        js.fromDay,
        js.toDay,
        dateType,
        selectedHotels,
        selectedCurrencies,
        accountManagersHotels,
    ]);

    const topClientsData = useMemo(() => {
        if (dataCommission.status !== "loaded") return {};
        const { fromDay, toDay } = getFromTo(js);
        if (!fromDay || !toDay) return {};

        const monthSpanLocal = buildMonthSpan(fromDay, toDay);
        return buildTopCommissionClientsData({
            dataCommission,
            dateType,
            monthSpan: monthSpanLocal,
            selectedHotels,
            selectedCurrencies,
            accountManagersHotels,
            metricKey: "commissionNotCancelled",
        });
    }, [
        dataCommission,
        js.fromDay,
        js.toDay,
        dateType,
        selectedHotels,
        selectedCurrencies,
        accountManagersHotels,
    ]);

    const stackedMonthData = useMemo(() => {
        if (dataCommission.status !== "loaded") return {};
        const { fromDay, toDay } = getFromTo(js);
        if (!fromDay || !toDay) return {};

        return buildMonthlyStackedData({
            dataCommission,
            dateType,
            monthSpan: buildMonthSpanAllYears(dataCommission.data ?? {}),
            selectedHotels,
            selectedCurrencies,
            accountManagersHotels,
            metricKey: "commissionNotCancelled",
        });
    }, [
        dataCommission,
        js.fromDay,
        js.toDay,
        dateType,
        selectedHotels,
        selectedCurrencies,
        accountManagersHotels,
    ]);

    const commissionProcessFn = useMemo(
        () =>
            ({ js: fnJs }) => {
                if (dataCommission.status !== "loaded")
                    return { entries: {}, len: 0 };
                const { fromDay, toDay } = getFromTo(fnJs);
                if (!fromDay || !toDay) return { entries: {}, len: 0 };

                const monthSpan = buildMonthSpan(fromDay, toDay);
                const entries = {};

                monthSpan.forEach(({ year, month }) => {
                    const {
                        total,
                        rooms,
                        vouchers,
                        totalLY,
                        roomsLY,
                        vouchersLY,
                    } = computeMonthCommission(year, month, {
                        dataCommission,
                        dateType,
                        selectedHotels,
                        selectedCurrencies,
                        accountManagersHotels,
                    });

                    const monthStart = dayjs.utc(
                        `${year}-${String(month + 1).padStart(2, "0")}-01`,
                    );
                    const monthEnd = monthStart.endOf("month");
                    const lyStart = monthStart.subtract(1, "year");
                    const lyEnd = monthEnd.subtract(1, "year");
                    const key = `${year}|${String(month + 1).padStart(2, "0")}`;

                    entries[key] = {
                        result: {
                            commissionNotCancelled: total,
                            commissionRoomsNotCancelled: rooms,
                            commissionVouchers: vouchers,
                            fromDateGroup: monthStart.format("YYYY-MM-DD"),
                            toDateGroup: monthEnd.format("YYYY-MM-DD"),
                        },
                        resultOneYearBefore: {
                            commissionNotCancelled: totalLY,
                            commissionRoomsNotCancelled: roomsLY,
                            commissionVouchers: vouchersLY,
                            fromDateGroup: lyStart.format("YYYY-MM-DD"),
                            toDateGroup: lyEnd.format("YYYY-MM-DD"),
                        },
                        keys: [
                            [GROUP_DATE_YEAR, String(year)],
                            [
                                GROUP_DATE_MONTH,
                                String(month + 1).padStart(2, "0"),
                            ],
                        ],
                        data: [],
                        comparisonMetaByMetric: {},
                    };
                });

                return { entries, len: 0 };
            },
        [
            dataCommission,
            dateType,
            selectedHotels,
            selectedCurrencies,
            accountManagersHotels,
        ],
    );

    const tableData = useMemo(() => {
        if (dataCommission.status !== "loaded") return [];
        // console.log({ dataCommission });
        const yearsObj = dataCommission.data ?? {};
        const dateField = dateType; // use string field name instead of numeric constant
        const rows = [];
        Object.keys(yearsObj).forEach((yearStr) => {
            const yData = yearsObj[yearStr]?.data ?? {};
            const year = Number(yearStr);

            Object.values(yData).forEach((siteObj) => {
                const siteID = siteObj?.SiteIDs?.[0] ?? siteObj?.SiteID;
                const hotelID = siteObj?.HotelID;

                if (!siteID) return; // skip non-hotel nodes

                const siteName = getDisplayName(siteObj, siteID);
                const currency = siteObj.base_currency ?? "EUR";
                const currencySymbol = currenciesMap[currency];
                if (selectedHotels.length && !selectedHotels.includes(siteName))
                    return;
                if (
                    selectedCurrencies.length &&
                    !selectedCurrencies.includes(currency)
                )
                    return;
                if (
                    accountManagersHotels.length &&
                    !accountManagersHotels.includes(hotelID)
                )
                    return;

                const section = siteObj[dateField] ?? {};
                const roomsArr =
                    (dateType === DATATYPE_RESDATE
                        ? section.RoomsCommission_All
                        : section.RoomsCommission_NoCanc) ?? [];
                const vouchersArr =
                    (dateType === DATATYPE_RESDATE
                        ? section.VouchersCommission_All
                        : section.VouchersCommission_NoCanc) ?? [];

                roomsArr.forEach((val, idx) => {
                    if (!val) return;
                    const value = val;
                    rows.push({
                        SiteName: siteName,
                        CurrencyCode: currency,
                        CurrencySymbol: currencySymbol,
                        AroTotal: value,
                        ResTypeID: 2, // Rooms
                        CancelStatus: 0,
                        [dateField]: `${year}-${String(idx + 1).padStart(
                            2,
                            "0",
                        )}-01`,
                        SiteID: siteID,
                        HotelID: siteObj?.HotelID,
                    });
                });

                vouchersArr.forEach((val, idx) => {
                    if (!val) return;
                    const value = val;
                    rows.push({
                        SiteName: siteName,
                        CurrencyCode: currency,
                        CurrencySymbol: currencySymbol,
                        AroTotal: value,
                        ResTypeID: 4, // Vouchers
                        CancelStatus: 0,
                        [dateField]: `${year}-${String(idx + 1).padStart(
                            2,
                            "0",
                        )}-01`,
                        SiteID: siteID,
                        HotelID: siteObj?.HotelID,
                    });
                });
            });
        });

        return rows;
    }, [
        dataCommission,
        dateType,
        selectedHotels,
        selectedCurrencies,
        selectedAccountManagers,
        accountManagersHotels,
    ]);
    return (
        <Container maxWidth={false}>
            <Stack sx={{ pt: 2, pb: 2 }} spacing={2}>
                <Typography variant={"h4"}>Commission Report</Typography>
                <Typography variant={"h8"}>
                    This report displays commission amounts generated through
                    each hotel's booking engine.
                    <br />
                    Users can filter the data by Booked On, Checked In, or
                    Checked Out dates.
                    <ul>
                        <li>Booked data includes cancellations.</li>
                        <li>
                            Checked In and Checked Out data exclude
                            cancellations.
                        </li>
                    </ul>
                    Additional filters include Hotel and Hotel Currency.
                    <br />
                    All amounts are displayed in Euros (€), with the original
                    hotel currency shown for reference only.
                </Typography>
                <DataLoader Commission />
            </Stack>

            <Grid
                container
                direction="row"
                justifyContent="flex-start"
                alignItems="flex-end"
                spacing={1}
                sx={{ pb: 2 }}
            >
                <Grid item>
                    <ToggleButtonTitled
                        TITLE={"Show commission by"}
                        values={{
                            [DATATYPE_RESDATE]: DATATYPE_RESDATE_NAME,
                            [DATATYPE_CHECKIN]: DATATYPE_CHECKIN_NAME,
                            [DATATYPE_CHECKOUT]: DATATYPE_CHECKOUT_NAME,
                        }}
                        valueSet={dateType}
                        onChange={(e, v) => setDateType(v)}
                    />
                </Grid>

                <Grid item>
                    <Typography>Time period</Typography>
                    <FormControl size="small">
                        <Select
                            labelId="timePeriodLabel"
                            id="timePeriodSelect"
                            value={js.presetDate ?? PRESET_CUSTOM}
                            onChange={(e, v) => {
                                const chosen = v.props.value;
                                if (chosen === PRESET_CUSTOM) {
                                    // Open picker and default to current year/month
                                    const { fromDay, toDay } = PresetDates(
                                        PRESET_THIS_YEAR,
                                        {
                                            year: new Date().getUTCFullYear(),
                                            month: new Date().getUTCMonth(),
                                        },
                                    );
                                    dispatch(
                                        setJS({
                                            ...js,
                                            fromDay,
                                            toDay,
                                            presetDate: chosen,
                                        }),
                                    );
                                    setOpenDatePicker(true);
                                } else {
                                    setOpenDatePicker(false);
                                    const { fromDay, toDay } = PresetDates(
                                        chosen,
                                        {
                                            year: new Date().getUTCFullYear(),
                                            month: new Date().getUTCMonth(),
                                        },
                                    );
                                    dispatch(
                                        setJS({
                                            ...js,
                                            fromDay,
                                            toDay,
                                            presetDate: chosen,
                                        }),
                                    );
                                }
                            }}
                            sx={{ height: ITEM_HEIGHT }}
                            MenuProps={{ sx: { maxHeight: "280px" } }}
                        >
                            {[PRESET_THIS_YEAR, ...PRESET_BOOKING_GROUP].map(
                                (e) => (
                                    <MenuItem key={e} value={e}>
                                        {e}
                                    </MenuItem>
                                ),
                            )}
                            <MenuItem value={PRESET_CUSTOM}>Custom</MenuItem>
                        </Select>
                    </FormControl>
                </Grid>

                <Grid item>
                    <Typography>Custom date</Typography>
                    <DateRangePicker
                        pickByMonthYear
                        boxProps={{ sx: { height: ITEM_HEIGHT } }}
                        fromDateProps={{
                            value: dayjs(js.fromDay),
                            minDate: minYear
                                ? dayjs.utc(`${minYear}-01-01`)
                                : undefined,
                            maxDate: maxYearNext
                                ? dayjs.utc(`${maxYearNext}-01-00`)
                                : undefined,
                        }}
                        isRangePicker={js.presetDate !== PRESET_CHOOSE_MONTH}
                        onChangeFromDate={(newValue) => {
                            const chooseMonthToDay =
                                js.presetDate !== PRESET_CHOOSE_MONTH
                                    ? js.toDay
                                    : PresetDates(PRESET_CHOOSE_MONTH, {
                                          year: newValue.year(),
                                          month: newValue.month(),
                                      }).toDay;
                            dispatch(
                                setJS({
                                    ...js,
                                    fromDay: formatDate(newValue),
                                    ...(js.presetDate !== PRESET_CHOOSE_MONTH
                                        ? { presetDate: PRESET_CUSTOM }
                                        : { toDay: chooseMonthToDay }),
                                }),
                            );
                        }}
                        toDateProps={{
                            value: dayjs(js.toDay),
                            maxDate: maxYearNext
                                ? dayjs.utc(`${maxYearNext}-01-01`)
                                : undefined,
                        }}
                        onChangeToDate={(newValue) => {
                            dispatch(
                                setJS({
                                    ...js,
                                    toDay: formatDate(newValue),
                                    presetDate: PRESET_CUSTOM,
                                }),
                            );
                        }}
                        open={openDatePicker}
                    />
                </Grid>

                <Grid item>
                    <Typography>Filters</Typography>
                    <FiltersByClient
                        height={ITEM_HEIGHT}
                        hotelsList={hotelsList}
                        selectedHotels={selectedHotels}
                        setSelectedHotels={setSelectedHotels}
                        handleSelectedHotels={handleSelectedHotels}
                        currencies={currenciesMap}
                        selectedCurrencies={selectedCurrencies}
                        setSelectedCurrencies={setSelectedCurrencies}
                        handleSelectedCurrencies={handleSelectedCurrencies}
                        accountManagers={accountManagers} // Options
                        selectedAccountManagers={selectedAccountManagers} // Selected
                        setSelectedAccountManagers={setSelectedAccountManagers} // SetSelected
                        handleSelectedAccountManagers={
                            handleSelectedAccountManagers
                        } //handleSelected
                    />
                </Grid>
            </Grid>

            <ActiveFiltersLine
                filters={{
                    Hotels: { label: "Hotel" },
                    Currencies: {
                        label: "Currency",
                        format: (code) =>
                            `${code} (${currenciesMap?.[code] ?? "?"})`,
                    },
                    AccountManagers: {
                        label: "AM",
                        format: (selected) => accountManagers[selected].name,
                    },
                }}
                activeFilters={{
                    Hotels: selectedHotels,
                    Currencies: selectedCurrencies,
                    AccountManagers: selectedAccountManagers,
                }}
                onActiveFiltersChange={({
                    Hotels = [],
                    Currencies = [],
                    AccountManagers = [],
                }) => {
                    setSelectedHotels(Hotels);
                    setSelectedCurrencies(Currencies);
                    setSelectedAccountManagers(AccountManagers);
                    handleSelectedHotels(Hotels);
                    handleSelectedCurrencies(Currencies);
                    handleSelectedAccountManagers(AccountManagers);
                }}
                sx={{ mt: 1 }}
            />

            {/* Summary */}
            <br />
            <ReportTableMetricsSummary
                data={{
                    "": {
                        keys: [["Month", "Year"]],
                        result: {
                            commissionNotCancelled:
                                commissionGlobal.commissionTotal,
                            commissionRoomsNotCancelled:
                                commissionGlobal.commissionRooms,
                            commissionVouchers:
                                commissionGlobal.commissionVouchers,
                        },
                        resultOneYearBefore: {
                            commissionNotCancelled:
                                commissionGlobal.commissionTotalLastYear,
                            commissionRoomsNotCancelled:
                                commissionGlobal.commissionRoomsLastYear,
                            commissionVouchers:
                                commissionGlobal.commissionVouchersLastYear,
                        },
                        data: [],
                    },
                }}
                js={{
                    ...js,
                    filters: {},
                    hideFields: [],
                    fieldsOrder: [
                        "commissionNotCancelled",
                        "commissionRoomsNotCancelled",
                        "commissionVouchers",
                    ],
                }}
                processFn={commissionProcessFn}
                hideExtra
                place="CommissionScreen"
            />

            {/* Top clients */}
            <ReportChartBarsCategory
                data={topClientsData}
                entriesLimit={5}
                entriesLimitSort="descending"
                initialMetric="commissionNotCancelled"
                metrics={{
                    commissionNotCancelled: {
                        label: "Commission",
                        short: "Comm.",
                        format: { left: "currency" },
                    },
                }}
                groupBy={["SiteName"]}
                title="Top Commission Clients"
                showVouchers={false}
                currency={"€"}
            />

            {/* Stacked monthly chart */}
            <ReportChartStackedMonth
                data={stackedMonthData}
                metric="commissionNotCancelled"
                datatype={dateType}
                fromDay={js.fromDay}
                toDay={js.toDay}
                title="Commission breakdown by month"
                useRangeMonths
                useAllYears
            />

            <br />
            <CommissionTable
                datatype={dateType}
                js={js}
                data={tableData}
                selectedHotels={selectedHotels}
                selectedCurrencies={selectedCurrencies}
                currencies={currenciesMap}
            />
        </Container>
    );
};

export default CommissionScreen2;
